import { Button as BaseButton } from '@base-ui/react/button'
import type { ComponentProps, MouseEvent, ReactNode } from 'react'
import { cx } from '../cx.js'
import styles from './Button.module.scss'

/**
 * Button — the section actions, the primary action of a screen, and the
 * triggers of the other primitives.
 *
 * Wraps `@base-ui/react` Button, which renders a native `<button>` and adds
 * one thing a native button lacks: `focusableWhenDisabled`. A disabled Button
 * stays in the tab order and announces itself as unavailable (`aria-disabled`)
 * instead of vanishing from assistive technology; it never activates, and a
 * click does not focus it. `loading` is the same inert state with `aria-busy`.
 *
 * Three variants, no more: `primary` (the one accent-filled action a screen
 * may have), `secondary` (a hairline box), `quiet` (text only — the section
 * actions). Two sizes. Tokens only; hover and press move the surface one step
 * along the ramp and nothing changes size or position.
 */
export type ButtonProps = {
  variant?: 'primary' | 'secondary' | 'quiet' | undefined
  size?: 'sm' | 'md' | undefined
  /** Unavailable, but still discoverable: keyboard-focusable and announced. */
  disabled?: boolean | undefined
  /** Working on the last press: inert and `aria-busy`. */
  loading?: boolean | undefined
  /** A leading glyph. Decorative: the label carries the meaning. */
  icon?: ReactNode
  children: ReactNode
} & Omit<ComponentProps<'button'>, 'className' | 'children' | 'disabled' | 'style'>

const VARIANT = {
  primary: styles.variantPrimary,
  secondary: styles.variantSecondary,
  quiet: styles.variantQuiet,
} as const

const SIZE = { sm: styles.sizeSm, md: styles.sizeMd } as const

export function Button({
  variant = 'secondary',
  size = 'md',
  disabled = false,
  loading = false,
  icon,
  children,
  type = 'button',
  onMouseDown,
  ...rest
}: ButtonProps) {
  const inert = disabled || loading
  return (
    <BaseButton
      {...rest}
      type={type}
      disabled={inert}
      focusableWhenDisabled
      aria-busy={loading || undefined}
      onMouseDown={(event: MouseEvent<HTMLButtonElement>) => {
        // Focus follows a press on a native button; an inert one should not take it.
        if (inert) event.preventDefault()
        onMouseDown?.(event)
      }}
      className={(state) =>
        cx(
          styles.root,
          VARIANT[variant],
          SIZE[size],
          state.disabled && styles.isDisabled,
          loading && styles.isLoading,
        )
      }
    >
      {icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className={styles.label}>{children}</span>
    </BaseButton>
  )
}
